import type { SpecContent } from './api/types.js';

/** Renders a specification's content as a Markdown document. */
export function specToMarkdown(content: SpecContent, title: string): string {
  const bullets = (items: string[]): string =>
    items.length ? items.map((i) => `- ${i}`).join('\n') : '_None_';

  const sections = [
    `# ${title} — Specification`,
    `## Context\n\n${content.context}`,
    `## Business problem\n\n${content.business_problem}`,
    `## Target users\n\n${content.target_users}`,
    `## Functional requirements\n\n${bullets(content.functional_requirements)}`,
    `## Non-functional requirements\n\n${bullets(content.non_functional_requirements)}`,
    `## Time to market\n\n${content.time_to_market}`,
    `## Branding\n\n${content.branding}`,
  ];

  if (content.suggestions.length) {
    const items = content.suggestions
      .map((s) => `### ${s.kind}: ${s.title}\n\n${s.rationale}`)
      .join('\n\n');
    sections.push(`## Suggestions\n\n${items}`);
  }

  return `${sections.join('\n\n')}\n`;
}

/** Triggers a browser download of `text` as a file named `filename`. */
export function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Slugifies a project name into a safe file stem. */
export function fileStem(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'specification'
  );
}
